/**
 * @module adyen
 * @category widgets
 * @subcategory int_adyen_ext
 * @description Represents Adyen helper that used for getting Adyen Checkout package
 *
 * @example <caption>Example of usage</caption>
 * import { getAdyenPackage } from 'adyenExt/toolbox/adyen';
 * getAdyenPackage().then(adyenPackage => {
 *     // process "adyenPackage"
 * });
 */

import type { AdyenCheckout, CoreConfiguration, ICore, PayPalConfiguration, UIElement } from '@adyen/adyen-web/auto';

type TAdyenPackage = typeof import('@adyen/adyen-web/auto');

export type TAdyenCore = ICore;
export type TAdyenCoreConfiguration = CoreConfiguration;
export type TAdyenCheckout = typeof AdyenCheckout;
export type TPayPalConfiguration = PayPalConfiguration;
export type TComponent = UIElement<TPayPalConfiguration>;

type TAdyenComponentConstructor = new (checkout: TAdyenCore, props?: object) => UIElement;

declare global {
    interface Window {
        AdyenConfiguration?: TAdyenCoreConfiguration;
        AdyenCheckout: TAdyenCheckout;
        paymentsDetailsURL: string;
        showStoreDetails?: boolean;
    }
}

export function getAdyenPackage() {
    return import(/* webpackChunkName: 'adyen-checkout' */ '@adyen/adyen-web/auto');
}

export function getAdyenCheckout() {
    return getAdyenPackage().then(d => d.AdyenCheckout);
}

export function initializeAdyenCheckout(configuration: object) {
    return getAdyenCheckout().then(AdyenCheckout => AdyenCheckout(configuration as CoreConfiguration));
}

export function getAdyenComponentConstructor(adyenPackage: TAdyenPackage, paymentType: string): TAdyenComponentConstructor | undefined {
    const componentMap = {
        scheme: adyenPackage.Card,
        card: adyenPackage.Card,
        paypal: adyenPackage.PayPal,
        googlepay: adyenPackage.GooglePay,
        paywithgoogle: adyenPackage.GooglePay,
        applepay: adyenPackage.ApplePay,
        klarna: adyenPackage.Klarna,
        klarna_paynow: adyenPackage.Klarna,
        klarna_account: adyenPackage.Klarna,
        blik: adyenPackage.Blik,
        giftcard: adyenPackage.Giftcard
    };

    return componentMap[paymentType];
}

export function createAdyenComponent(adyenPackage: TAdyenPackage, adyenCheckoutInstance: TAdyenCore, paymentType: string, props?: object) {
    const ComponentConstructor = getAdyenComponentConstructor(adyenPackage, paymentType) || adyenCheckoutInstance.getComponent(paymentType);

    if (!ComponentConstructor) {
        throw new Error(`Unsupported Adyen component type: ${paymentType}`);
    }

    return new ComponentConstructor(adyenCheckoutInstance, props);
}
